import { createSelector } from 'reselect'
import path from 'ramda/es/path'

import { APP_POLLS, APP_ACCESS, APP_DNEVNIK, APP_CAFETERIA } from 'app/common/constants/services'
import { getIsStudent } from 'app/selectors/user'
import * as orgsTypes from 'app/common/constants/schools/types'


/**
* @param {Number|String} id: идентификатор студента, для которого проверяется подключение
* @param {String} app: символьное имя приложения
* @returns {Object|undefined}
*/

const selectSubId = (state, { app, id }) => path(['apps', 'data', app, id], state)
const selectSubIdAndTariff = createSelector(
  [
    selectSubId,
    state => state.apps.tariffs,
    state => state.apps.subs,
  ],
  (subId, tariffs, subs) => {
    const tariffId = subId && path([subId, 'tariff_id'], subs)
    const modified = subId && path([subId, 'modified'], subs)
    const tariff = tariffId && tariffs[tariffId]
    return ({
      subId,
      tariff: (tariff && modified
        ? { ...tariff, ...modified }
        : tariff
      ),
    })
  }
)

/**
* Есть ли подписка на приложение (относительно РЕБЕНКА)
* если есть, то возвращается объект подписки
* @param {Number|String} id: идентификатор студента, для которого проверяется подключение
* @param {String} app: символьное имя приложения
* @returns {Object}
*/

export const getIsAppSubscribed = createSelector(
  [
    (_, { id }) => (typeof id === 'number' ? id : parseInt(id, 10)),
    (_, { app }) => app,
    (state, { app }) => state.apps.data[app],
    state => state.apps.subs,
    getIsStudent,
    state => state.students.data,
    selectSubIdAndTariff,
  ],
  (id, app, data, subs, isStudent, studentsData, { subId, tariff }) => {
    // ЗДЕСЬ ДУБЛИРУЕТСЯ С Components/Subs/AssignedList/selectAssignedList
    const currentOrgType = path([id, 'orgType'], studentsData)

    // Голосования TODO
    if (app === APP_POLLS) {
      return (data && data.students && data.students.includes(id)
        ? { is_active: true, lk_paid: true }
        : null
      )
    }
    // Проходная при логине ребенка (отключена) (c 14.12.2020 только для заведений типа школа и садик)
    if (app === APP_ACCESS
      && (currentOrgType === orgsTypes.SCHOOL || currentOrgType === orgsTypes.PRESCHOOL)
      && isStudent) {
      return null
    }
    // Столовая не поддерживается в школе ученика
    if (app === APP_CAFETERIA && !path([id, 'hasCafeteria'], studentsData)) {
      return null
    }
    // Дневник не поддерживается в школе (любой)
    if (app === APP_DNEVNIK && !path([id, 'hasDnevnik'], studentsData)) {
      return null
    }
    // не найден  тариф
    if (!tariff && !subs[subId]?.free_services) return null
    // тариф не поддерживает личный кабинет
    if (!tariff?.lk_enabled && !subs[subId].free_services) return null

    return subs[subId]
  }
)

/**
* Активна ли подписка на приложение (относительно РЕБЕНКА)
* НЕ для раздела "Все приложения", только для отображения конкретного приложения
* @param {Number|String} id: идентификатор студента, для которого проверяется подключение
* @param {String} app: символьное имя приложения
* @returns {boolean}
*/

export const getIsAppConnected = createSelector(
  [
    getIsAppSubscribed,
  ],
  sub => {
    return !!(sub
      && sub.is_active // Приложение активно (списывается плата)
      && !sub.is_paused // Приложение не приостановлено
      && (sub.lk_paid || sub.test_days_left) // оплачен ЛК в подписке или тестовый период
    ) || !!(sub && sub.free_services)
  } // бесплатное предоставление сервисов в МП и ЛК
)

/**
* Есть ли подписка на приложение Опросник, относительно ШКОЛЫ
* Для конкретного приложения, относительно школы
* @param {Number} id: идентификатор школы, для которой проверяется подписка
* @returns {boolean}
*/

export const getIsAppPollsConnected = createSelector(
  [
    (_, { id }) => (typeof id === 'number' ? id : parseInt(id, 10)),
    state => state.apps.data,
    state => state.students.data,
  ],
  (id, data, students) => !!(students && data.polls && data.polls.students
    && data.polls.students.some(studentId => students[studentId].schoolId === id))
)
